"use client";

import React, { useEffect, useState } from "react";
import { Search, User, Menu, ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger, NavigationMenuContent } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { CartIcon } from "@/components/shop/CartIcon";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";

// Recursive navigation sub-menu renderer for desktop
function DesktopSubMenu({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className="flex flex-col gap-1 min-w-[200px] p-1">
      {items.map((child, i) => <li key={child.id} data-index={i} className="relative group/sub">
          {child.children && child.children.length > 0 ? <div className="flex flex-col">
              <div className="flex items-center justify-between px-3 py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                <span>{child.label}</span>
                <ChevronRight className="size-3 text-muted-foreground/70" />
              </div>
              <div className="pl-2 border-l border-border/40 ml-2">
                <DesktopSubMenu items={child.children} depth={depth + 1} />
              </div>
            </div> : <NavigationMenuLink asChild>
              <Link to={child.href || "#"} className="block px-3 py-2 text-sm text-foreground/80 hover:text-foreground hover:bg-accent/40 rounded-sm transition-colors">
                {child.label}
              </Link>
            </NavigationMenuLink>}
        </li>)}
    </ul>;
}

// Desktop Menu Consumer
function DesktopNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex items-center gap-2">
        <div className="h-8 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-8 w-20 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-8 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="flex items-center gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-foreground/90 hover:text-foreground hover:bg-accent/30 text-xs font-semibold uppercase tracking-wider h-9 px-3">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="bg-popover border border-border/80 shadow-md rounded-md p-1 min-w-[220px]">
                  <DesktopSubMenu items={item.children} />
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-foreground/80 hover:text-foreground hover:bg-accent/30 rounded-md transition-colors h-9">
                  {item.label}
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}

// Recursive navigation sub-menu renderer for mobile drawer
function MobileNavBranch({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className="flex flex-col gap-1 w-full">
      {items.map((item, i) => <li key={item.id} data-index={i} className="flex flex-col w-full">
          {item.children && item.children.length > 0 ? <div className="w-full">
              <div className="flex items-center justify-between py-2 text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                <span>{item.label}</span>
                <ChevronDown className="size-3.5 text-muted-foreground/60" />
              </div>
              <div className="pl-3 border-l border-border/40 ml-1">
                <MobileNavBranch items={item.children} depth={depth + 1} />
              </div>
            </div> : <Link to={item.href || "#"} className="py-2.5 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
              {item.label}
            </Link>}
        </li>)}
    </ul>;
}

// Mobile Menu Consumer
function MobileNavConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-3 py-4">
        <div className="h-6 w-32 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-6 w-40 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-6 w-28 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="w-full flex flex-col py-2">
      <MobileNavBranch items={menuItems} />
    </nav>;
}
export default function Header() {
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const accountUrl = useWooAccountUrl();

  // Smooth scroll for anchor links
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            const headerOffset = 80;
            const top = element.getBoundingClientRect().top + window.pageYOffset - headerOffset;
            window.scrollTo({
              top,
              behavior: "smooth"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    window.location.assign(`/ShopPage?s=${encodeURIComponent(searchQuery.trim())}`);
  };
  return <section id="header" data-nav="dark" className="sticky top-0 z-50 dark bg-background text-foreground border-b border-border/40 backdrop-blur-md transition-colors">
      <div className="max-w-[1088px] mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between gap-4">
        {/* Brand Logo strictly isolated */}
        <div className="shrink-0 flex items-center">
          <Link to="/" aria-label="ResearchPeptide Homepage" className="inline-block">
            <WvcLogo className="h-12 w-auto" />
          </Link>
        </div>

        {/* Central Primary Navigation (Desktop) */}
        <div className="hidden lg:flex items-center justify-center flex-1">
          <MenuProvider menu_id="22">
            <DesktopNavConsumer />
          </MenuProvider>
        </div>

        {/* Right-hand Action Cluster */}
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Quick Catalogue Search Bar (Desktop) */}
          <form onSubmit={handleSearchSubmit} className="hidden md:flex relative items-center w-48 xl:w-56">
            <Input type="search" name="s" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search catalog..." className="h-9 pr-8 pl-3 text-xs bg-muted/20 border-border/50 text-foreground placeholder:text-muted-foreground/70 rounded-md focus-visible:ring-primary" />
            <Button type="submit" variant="ghost" size="icon" className="absolute right-0 top-0 h-9 w-8 text-muted-foreground hover:text-foreground" aria-label="Submit search">
              <Search className="size-3.5" />
            </Button>
          </form>

          {/* Account Profile Link */}
          <Button asChild variant="ghost" size="icon" className="text-foreground/80 hover:text-foreground hover:bg-accent/40 rounded-md size-9">
            <Link to={accountUrl} aria-label="Account Dashboard">
              <User className="size-4" aria-hidden="true" />
            </Link>
          </Button>

          {/* Cart Trigger with Real-Time Counter Badge */}
          <CartIcon variant="ghost" size="icon" iconType="cart" iconSize="size-4" className="text-foreground/80 hover:text-foreground hover:bg-accent/40 rounded-md size-9" />

          {/* Mobile Navigation Drawer Trigger */}
          <div className="flex lg:hidden">
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="size-9 text-foreground/80 hover:text-foreground hover:bg-accent/40 rounded-md" aria-label="Open mobile menu">
                  <Menu className="size-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-full sm:max-w-sm bg-background border-l border-border/40 p-6 flex flex-col justify-between">
                <div>
                  <SheetHeader className="mb-6 p-0 text-left">
                    <SheetTitle className="text-base font-semibold tracking-wide uppercase text-foreground">
                      Navigation
                    </SheetTitle>
                  </SheetHeader>

                  {/* Mobile Search Query */}
                  <form onSubmit={handleSearchSubmit} className="relative mb-6">
                    <Input type="search" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search peptides & documentation..." className="h-10 pr-9 pl-3 text-sm bg-muted/20 border-border/60 text-foreground rounded-md" />
                    <Button type="submit" variant="ghost" size="icon" className="absolute right-0 top-0 h-10 w-9 text-muted-foreground hover:text-foreground" aria-label="Search">
                      <Search className="size-4" />
                    </Button>
                  </form>

                  {/* Mobile Navigation Items */}
                  <div className="overflow-y-auto max-h-[calc(100vh-220px)] pr-2">
                    <MenuProvider menu_id="22">
                      <MobileNavConsumer />
                    </MenuProvider>
                  </div>
                </div>

                {/* Mobile Drawer Footer with RUO Notice */}
                <div className="pt-4 border-t border-border/30 text-xs text-muted-foreground">
                  <p className="font-mono uppercase tracking-wider text-[11px] text-foreground/70 mb-1">
                    Research Use Only (RUO)
                  </p>
                  <p>Certified lab supplier serving Indian institutions.</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}